import 'src/i18n/config';

import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';

import { MotionLazyContainer } from './components/animate';
import { queryClient } from './lib/queryClient';
import { setRootStoreSingleton } from './models/root-store/root-store-singleton';
import { initializeGA4 } from './analytics';
import { RootStore, RootStoreProvider, setupRootStore } from './models';
import router from './routes';
import ThemeProvider from './theme';

function App() {
  const [rootStore, setRootStore] = useState<RootStore | undefined>(undefined);
  const { i18n } = useTranslation();

  useEffect(() => {
    initializeGA4(process.env.REACT_APP_GA4_MEASUREMENT_ID);
  }, []);

  useEffect(() => {
    // setup root store
    setupRootStore().then((store) => {
      setRootStore(store);
      setRootStoreSingleton(store);
      store.authStore.getUserProfile();
    });
  }, []);

  // 언어 변경 시 locale 변경
  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  if (!rootStore) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RootStoreProvider value={rootStore}>
        <HelmetProvider>
          <ThemeProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MotionLazyContainer>
                <RouterProvider router={router} />
              </MotionLazyContainer>
            </LocalizationProvider>
          </ThemeProvider>
        </HelmetProvider>
      </RootStoreProvider>
      {import.meta.env.DEV && window.location.hostname === 'localhost' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}

export default App;
